import { Box, Button, Typography } from '@mui/material'
import { useEffect, useRef, useState } from 'react'
import { COLORS } from '~theme/colors'
import { FileRenderer } from './uploaded-file'
import { ExportIcon } from 'src/components/icons'
import { ALLOWED_EXTENSIONS } from '../../documents-page/modals/add-document-modal'

const label = 'Click here or drag and drop to add file(s)'
const containerStyle = {
  border: `1px dashed ${COLORS.greys.mid}`,
  borderRadius: '4px',
  width: '100%',
  height: '104px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  textAlign: 'center',
}

export function DragAndDropFiles({
  onChangeHandler,
  isDisabled,
  singleFile,
}: {
  onChangeHandler?: (files: File[]) => void
  isDisabled?: boolean
  singleFile?: boolean
}) {
  if (onChangeHandler && !isDisabled) {
    return (
      <EnabledDragAndDropFiles
        onChangeHandler={onChangeHandler}
        singleFile={singleFile}
      />
    )
  }

  return <DisabledDragAndDropFiles />
}

function EnabledDragAndDropFiles({
  onChangeHandler,
  singleFile,
}: {
  onChangeHandler: (files: File[]) => void
  singleFile?: boolean
}) {
  const [files, setFiles] = useState<File[]>([])
  const fileInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    onChangeHandler(files)
  }, [files])

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    const droppedFiles = Array.from(event.dataTransfer.files)
    setFiles((prevFiles) => [...prevFiles, ...droppedFiles])
  }

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault()
  }

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(event.target.files || [])
    setFiles((prevFiles) => [...prevFiles, ...selectedFiles])
    event.target.value = ''
  }

  const handleClick = () => {
    fileInputRef?.current?.click()
  }

  const handleDelete = (index: number) => {
    setFiles((prevFiles) => prevFiles.filter((_, i) => i !== index))
  }

  return (
    <>
      <Box>
        <Box
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onClick={handleClick}
          sx={{ ...containerStyle, cursor: 'pointer' }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <ExportIcon size={32} />
            <Typography variant="paragraphBold1" sx={{ mt: 1 }}>
              {`Drag file${singleFile ? '' : '(s)'} here or click to browse`}
            </Typography>
            <Typography variant="paragraph1Secondary" sx={{ fontSize: '10px' }}>
              Supports{' '}
              {Array.from(ALLOWED_EXTENSIONS)
                .map((ext) => `.${ext}`)
                .join(', ')}{' '}
              files up to 20 MB.
            </Typography>
          </Box>
        </Box>
        <Box
          component="input"
          type="file"
          ref={fileInputRef}
          onChange={handleFileChange}
          sx={{ display: 'none' }}
          multiple={!singleFile}
        />
      </Box>

      {!!files?.length && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, py: 2 }}>
          {files?.map((file, i) => (
            <FileRenderer
              fileName={file.name}
              key={i}
              onDelete={() => {
                handleDelete(i)
              }}
            />
          ))}
        </Box>
      )}
    </>
  )
}

export function DisabledDragAndDropFiles() {
  return (
    <Box sx={{ ...containerStyle }}>
      <Typography variant="label" sx={{ color: COLORS.greys.mid }}>
        {label}
      </Typography>
    </Box>
  )
}
